import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {ContactRequest} from '@services/associations/model/contactRequest';
import {ContactResponse} from '@services/associations/model/contactResponse';
import {FeedAddRequest} from '@services/associations/model/feedAddRequest';
import {FeedEditRequest} from '@services/associations/model/feedEditRequest';
import {FeedResponse} from '@services/associations/model/feedResponse';
import {FeedSearchRequest} from '@services/associations/model/feedSearchRequest';
import {KeyWordDto} from '@services/associations/model/keyWordDto';
import {ManagerScheduleSettingsRequest} from '@services/associations/model/managerScheduleSettingsRequest';
import {ManagerScheduleSettingsResponse} from '@services/associations/model/managerScheduleSettingsResponse';
import {ManagerTaskScheduleResponse} from '@services/associations/model/managerTaskScheduleResponse';
import {OpenedTaskResponse} from '@services/associations/model/openedTaskResponse';
import {PageContactResponse} from '@services/associations/model/pageContactResponse';
import {PageFeedResponse} from '@services/associations/model/pageFeedResponse';
import {PageLeadResponse} from '@services/associations/model/pageLeadResponse';
import {PageNameResponse} from '@services/associations/model/pageNameResponse';
import {PageTaskResponse} from '@services/associations/model/pageTaskResponse';
import {ProjectRequest} from '@services/associations/model/projectRequest';
import {ProjectResponse} from '@services/associations/model/projectResponse';
import {ProjectSearchRequest} from '@services/associations/model/projectSearchRequest';
import {TaskBoardRequest} from '@services/associations/model/taskBoardRequest';
import {TaskChangeStatusRequest} from '@services/associations/model/taskChangeStatusRequest';
import {TaskListRequest} from '@services/associations/model/taskListRequest';
import {TaskRequest} from '@services/associations/model/taskRequest';
import {TaskResponse} from '@services/associations/model/taskResponse';
import {TaskSearchRequest} from '@services/associations/model/taskSearchRequest';
import {PaginationModel} from '@shared/models/pagination.model';
import {Observable} from 'rxjs';
import {PageResponseProjectResponse} from "@services/associations/model/pageResponseProjectResponse";
import {ProjectStatusHistoryResponse} from "@services/associations/model/projectStatusHistoryResponse";
import {ProjectChangeStatusRequest} from "@services/associations/model/projectChangeStatusRequest";
import {AccountantMonitorRequest} from "@services/back-office/model/accountantMonitorRequest";
import {PageAccountantMonitorModel} from "@services/back-office/model/pageAccountantMonitorModel";
import {ContactsFilter} from "@crm/models/ContactsFilter";
import {ProjectProgressModel} from "@services/associations/model/projectProgressModel";

@Injectable({
    providedIn: 'root'
})
export class CrmService {
    private baseUrl = environment.baseUrl;

    constructor(
        private http: HttpClient,
    ) {
    }

    /** Load List Managers Schedule Settings */
    loadListManagersScheduleSettings(managerId?: string): Observable<ManagerScheduleSettingsResponse[]> {
        const url = `${this.baseUrl}/associations/back_office/schedule/list${managerId ? '?managerId=' + managerId : ''}`;
        return this.http.get<ManagerScheduleSettingsResponse[]>(url);
    }

    /** Upsert List Managers Schedule Settings */
    upsertListManagersScheduleSettings(managerId: string, request: ManagerScheduleSettingsRequest): Observable<ManagerScheduleSettingsResponse> {
        const url = `${this.baseUrl}/associations/back_office/schedule/settings/${managerId}`;
        return this.http.put<ManagerScheduleSettingsResponse>(url, request);
    }

    /** Delete List Managers Schedule Settings */
    deleteListManagersScheduleSettings(managerId: string): Observable<void> {
        const url = `${this.baseUrl}/associations/back_office/schedule/settings/${managerId}`;
        return this.http.delete<void>(url);
    }

    loadManagerSchedule(managerId: string, date?: string): Observable<ManagerTaskScheduleResponse> {
        const url = `${this.baseUrl}/associations/back_office/schedule/${managerId}/${date}`;
        return this.http.get<ManagerTaskScheduleResponse>(url);
    }

    /** Load Board Tasks */
    loadLeadsBoardTasks(request: TaskBoardRequest): Observable<TaskResponse[]> {
        const url = `${this.baseUrl}/associations/back_office/task/board`;
        return this.http.post<TaskResponse[]>(url, request);
    }

    /** Load List Tasks */
    loadLeadsListTasks(request: TaskListRequest, pagination: PaginationModel, sortByDate?: boolean): Observable<PageTaskResponse> {
        const url = `${this.baseUrl}/associations/back_office/task/list?page=${pagination.number}&size=${pagination.size}&sort=${sortByDate ? 'date DESC' : ''}`;
        return this.http.post<PageTaskResponse>(url, request);
    }

    /** Search Tasks */
    searchTasks(request: TaskSearchRequest, pagination: PaginationModel): Observable<PageTaskResponse> {
        const url = `${this.baseUrl}/associations/back_office/task/search?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageTaskResponse>(url, request);
    }

    /** Delete List Tasks */
    deleteLeadsListTasks(taskId: string): Observable<void> {
        const url = `${this.baseUrl}/associations/back_office/task/${taskId}`;
        return this.http.delete<void>(url);
    }

    /** Add Task */
    addTask(request: TaskRequest): Observable<TaskResponse> {
        const url = `${this.baseUrl}/associations/back_office/task`;
        return this.http.post<TaskResponse>(url, request);
    }

    /** Edit Task */
    editTask(taskId: string, request: TaskRequest): Observable<TaskResponse> {
        const url = `${this.baseUrl}/associations/back_office/task/${taskId}`;
        return this.http.put<TaskResponse>(url, request);
    }

    /** Change Task Status */
    changeTaskStatus(taskId: string, request: TaskChangeStatusRequest): Observable<TaskResponse> {
        const url = `${this.baseUrl}/associations/back_office/task/${taskId}/status`;
        return this.http.put<TaskResponse>(url, request);
    }

    changeTaskResponsible(managerId: string, taskIds: string[]): Observable<void> {
        const url = `${this.baseUrl}/associations/back_office/task/manager/${managerId}`;
        return this.http.put<void>(url, taskIds);
    }

    /** Get Task by ID */
    getTask(taskId: string): Observable<TaskResponse> {
        const url = `${this.baseUrl}/associations/back_office/task/${taskId}`;
        return this.http.get<TaskResponse>(url);
    }

    getOpenedTaskCount(): Observable<OpenedTaskResponse[]> {
        const url = `${this.baseUrl}/associations/back_office/task/opened`;
        return this.http.post<OpenedTaskResponse[]>(url, {});
    }

    /** Delete Task for CRM items */
    deleteTask(taskId: string): Observable<void> {
        const url = `${this.baseUrl}/associations/back_office/task/${taskId}`;
        return this.http.delete<void>(url);
    }

    /** Load Projects */
    loadProjects(request: ProjectSearchRequest, pagination: PaginationModel): Observable<PageResponseProjectResponse> {
        const url = `${this.baseUrl}/associations/back_office/project/search?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageResponseProjectResponse>(url, request);
    }

    /** Load feed for CRM items */
    loadFeed(request: FeedSearchRequest, pagination: PaginationModel): Observable<PageFeedResponse> {
        const url = `${this.baseUrl}/associations/back_office/feed/search?page=${pagination.number}&size=${pagination.size}`;
        return this.http.post<PageFeedResponse>(url, request);
    }

    /** Add feed for CRM items */
    addFeed(request: FeedAddRequest): Observable<FeedResponse> {
        const url = `${this.baseUrl}/associations/back_office/feed`;
        return this.http.post<FeedResponse>(url, request);
    }

    /** Edit feed for CRM items */
    editFeed(feedId: string, request: FeedEditRequest): Observable<FeedResponse> {
        const url = `${this.baseUrl}/associations/back_office/feed/${feedId}`;
        return this.http.put<FeedResponse>(url, request);
    }

    togglePinFeed(feedId: string): Observable<FeedResponse> {
        const url = `${this.baseUrl}/associations/back_office/feed/${feedId}/top`;
        return this.http.get<FeedResponse>(url);
    }

    /** Delete feed for CRM items */
    deleteFeed(feedId: string): Observable<void> {
        const url = `${this.baseUrl}/associations/back_office/feed/${feedId}`;
        return this.http.delete<void>(url);
    }

    /** Add Projects */
    addProjects(request: ProjectRequest): Observable<ProjectResponse> {
        const url = `${this.baseUrl}/associations/back_office/project`;
        return this.http.post<ProjectResponse>(url, request);
    }

    saveProjectProgress(projectId: string, request: ProjectProgressModel[]): Observable<ProjectProgressModel[]> {
        const url = `${this.baseUrl}/associations/back_office/project/progress/${projectId}`;
        return this.http.post<ProjectProgressModel[]>(url, request);
    }

    /** Edit Projects */
    editProjects(projectId: string, request: ProjectRequest): Observable<ProjectResponse> {
        const url = `${this.baseUrl}/associations/back_office/project/${projectId}`;
        return this.http.put<ProjectResponse>(url, request);
    }

    /** Delete Projects */
    deleteProjects(projectId: string): Observable<void> {
        const url = `${this.baseUrl}/associations/back_office/project/${projectId}`;
        return this.http.delete<void>(url);
    }

    /** Get Projects by ID */
    getProjects(projectId: string): Observable<ProjectResponse> {
        const url = `${this.baseUrl}/associations/back_office/project/${projectId}`;
        return this.http.get<ProjectResponse>(url);
    }

    getProjectsStatuses(projectId: string): Observable<ProjectStatusHistoryResponse[]> {
        const url = `${this.baseUrl}/associations/back_office/project/status/${projectId}`;
        return this.http.get<ProjectStatusHistoryResponse[]>(url);
    }

    setProjectsStatus(projectId: string, request: ProjectChangeStatusRequest): Observable<ProjectStatusHistoryResponse[]> {
        const url = `${this.baseUrl}/associations/back_office/project/status/${projectId}`;
        return this.http.post<ProjectStatusHistoryResponse[]>(url, request);
    }

    deleteProjectsStatus(projectStatusId: string): Observable<ProjectStatusHistoryResponse[]> {
        const url = `${this.baseUrl}/associations/back_office/project/status/${projectStatusId}`;
        return this.http.delete<ProjectStatusHistoryResponse[]>(url);
    }

    addContactToProject(projectId: string, userId: string, contactType: string, allowCloseTaskId?: string): Observable<void> {
        const url = `${this.baseUrl}/associations/back_office/project/${projectId}/${userId}/${contactType}${allowCloseTaskId ? '?allowCloseTaskId=' + allowCloseTaskId : ''}`;
        return this.http.get<void>(url);
    }

    /** Load Contacts */
    loadContacts(request: ContactsFilter, pagination: PaginationModel): Observable<PageContactResponse> {
        const params = {
            ...pagination?.number && {page: pagination.number},
            size: pagination?.size ? pagination.size : 0,
            includeArchived: !!request?.includeArchived
        };
        const url = `${this.baseUrl}/associations/back_office/contact/search`;
        return this.http.post<PageContactResponse>(url, request, {params});
    }

    /** Get Contacts by ID */
    getContact(contactId: string): Observable<ContactResponse> {
        const url = `${this.baseUrl}/associations/back_office/contact/${contactId}`;
        return this.http.get<ContactResponse>(url);
    }

    /** Add Contact */
    addContact(request: ContactRequest): Observable<ContactResponse> {
        const url = `${this.baseUrl}/associations/back_office/contact`;
        return this.http.post<ContactResponse>(url, request);
    }

    /** Edit Contact */
    editContact(contactId: string, request: ContactRequest): Observable<ContactResponse> {
        const url = `${this.baseUrl}/associations/back_office/contact/${contactId}`;
        return this.http.put<ContactResponse>(url, request);
    }

    /** Delete Contact */
    deleteContact(contactId: string): Observable<void> {
        const url = `${this.baseUrl}/associations/back_office/contact/${contactId}`;
        return this.http.delete<void>(url);
    }

    /** Search Contact */
    searchContact(request: KeyWordDto, pagination?: PaginationModel): Observable<PageNameResponse> {
        const url = `${this.baseUrl}/associations/back_office/contact/search/simple?page=${pagination ? pagination.number : ''}&size=${pagination ? pagination.size : '50'}`;
        return this.http.post<PageNameResponse>(url, request);
    }

    /** Upload Leads */
    uploadLeads(): Observable<void> {
        const url = `${this.baseUrl}/associations/back_office/lead/import`;
        return this.http.get<void>(url);
    }

    getLeads(pagination?: PaginationModel, managerId?: string): Observable<PageLeadResponse> {
        const url = `${this.baseUrl}/associations/back_office/lead?page=${pagination ? pagination.number : ''}&size=${pagination ? pagination.size : '25'}${managerId ? '&managerId=' + managerId : ''}`;
        return this.http.get<PageLeadResponse>(url);
    }

    createTaskFromLead(leadId: string): Observable<TaskResponse> {
        const url = `${this.baseUrl}/associations/back_office/lead/${leadId}`;
        return this.http.get<TaskResponse>(url);
    }

    loadAccountantMonitor(request: AccountantMonitorRequest, pagination: PaginationModel): Observable<PageAccountantMonitorModel> {
        const url = `${this.baseUrl}/back-office/v1/manager/organization/accountant/monitor?page=${pagination ? pagination.number : ''}&size=${pagination ? pagination.size : '25'}`;
        return this.http.post<PageAccountantMonitorModel>(url, request);
    }
}
